import { ChangeDetectionStrategy, Component, computed, inject, signal, ViewContainerRef } from '@angular/core';
import { BrnDialogRef, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { DialogSheetComponent, dialogSheetHostClass, HlmDialogOpener } from '@lite-workspace/shared/helm-ui/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmSkeleton } from '@lite-workspace/shared/helm-ui/skeleton';
import { HlmSwitchImports } from '@lite-workspace/shared/helm-ui/switch';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { pluralRu } from '@lite-workspace/shared/format';
import { isFolderKind, itemNameOf } from '@lite-workspace/dto-design-system';
import { firstValueFrom } from 'rxjs';
import {
  projectPrintItems,
  resolveCopyPrintProduct,
  type UpsaleBillDto,
  type UpsaleCopyFormat,
  type UpsalePositionDto,
} from '@lite-workspace/dto-fotoclass-book';
import type { BulkDownloadAlbum } from './bulk-download-albums-dialog.component';
import { ApproveAlbumDialogComponent, type ApproveAlbumDialogContext } from './approve-album-dialog.component';
import { toastCopyRefreshFailure } from './album-copies-dialog.component';
import { formatMoney } from '../../upsale/ui/money.pipe';
import { ProjectsStore } from '../domain/+state/projects.store';
import { PrintCatalogFacade } from '../domain/application/print-catalog.facade';
import type { PhotoBookVariant } from '../domain/infrastructure/projects-api.service';

export interface PrintFilesLine {
  personId: string;
  name: string;
  qty: number;
  sum: number;
  paid: boolean;
  /** Copy in another print product than the album's; absent = album format. */
  format?: string;
  /** That copy's print product — the key of its copy row; set with `format`. */
  product?: string;
}

/** A counted extra-format copy whose copy row is not approved — the archive refuses it. */
export interface UnapprovedCopy {
  albumId: string;
  name: string;
  format: string;
  printProductId: string;
  /** What the gate line offers: `review` = fresh previews to check; `refresh` = none yet or stale after an album
   *  change — rebuild first; `building` = wait; `null` = row not read. */
  action: 'review' | 'refresh' | 'building' | null;
}

const GATE_ACTION: Record<PhotoBookVariant['state'], UnapprovedCopy['action']> = {
  none: 'refresh',
  changed: 'refresh',
  building: 'building',
  unreviewed: 'review',
  approved: null,
};

export interface PrintFilesData {
  /** Approved albums only — the archive refuses any other. */
  albums: BulkDownloadAlbum[];
  /** Copy positions of approved-album owners. */
  copies: PrintFilesLine[];
  /** Print positions of every student. */
  prints: PrintFilesLine[];
}

export interface PrintFilesDialogContext {
  /** `null` = the opener's gate refused and is closing this dialog. */
  data: Promise<PrintFilesData | null>;
}

export interface PrintFilesDialogResult {
  archive: 'albums' | 'prints';
  includeUnpaid: boolean;
}

export interface PrintFilesSection {
  head: string;
  /** Counted with the switch; zero = nothing to archive, no «Скачать». */
  count: number;
  /** «Папки · n» per project item holding approved albums; one item = none, the head says it. */
  items: string[];
  /** «Копии <format> · n» per copy format other than the album's, counted with the switch. */
  formats: string[];
  unpaid: string | null;
  rows: string[];
}

type Forms = [string, string, string];

const ALBUM_FORMS: Forms = ['альбом', 'альбома', 'альбомов'];
const COPY_FORMS: Forms = ['копия', 'копии', 'копий'];
const RETRY = 'Почему-то не удалось.. попробуйте снова';

export function printFilesData(
  albums: readonly BulkDownloadAlbum[],
  bills: readonly UpsaleBillDto[],
  names: ReadonlyMap<string, string>,
  /** The project's print product. */
  productId: string | undefined,
  /** `goods.copies.extra` of the project's upsale settings. */
  copyFormats: readonly UpsaleCopyFormat[],
): PrintFilesData {
  const approved = albums.filter(album => album.approved);
  // The album's own product first, as core reads it: an aborted product change leaves an album on the old one.
  const ownProduct = new Map(approved.map(album => [album.personId, album.printProductId || productId]));
  const lines = (kind: UpsalePositionDto['kind'], keep: (personId: string) => boolean): PrintFilesLine[] =>
    bills
      .filter(bill => keep(bill.personId))
      .flatMap(bill =>
        bill.positions
          .filter(p => p.kind === kind)
          .map(p => {
            // The archive's rule: a copy with no product of its own nor of its format row prints in the album format.
            const copyProduct = p.kind === 'copy' ? resolveCopyPrintProduct(p.content, copyFormats) : undefined;
            const extra = copyProduct && copyProduct !== ownProduct.get(bill.personId);
            return {
              name: names.get(bill.personId) ?? 'Имя не задано',
              qty: p.qty,
              sum: p.sum,
              paid: p.paid,
              format: extra ? p.content.format : undefined,
              product: extra ? copyProduct : undefined,
              personId: bill.personId,
            };
          }),
      );
  return { albums: approved, copies: lines('copy', id => ownProduct.has(id)), prints: lines('print', () => true) };
}

const qtyOf = (lines: readonly PrintFilesLine[]): number => lines.reduce((n, line) => n + line.qty, 0);

/** The project's items: product id → plural name («Папки», «Альбомы 21×30») in item order; `main` = the item of an
 *  album with no product of its own. */
export interface PrintFilesItems {
  main?: string;
  names: ReadonlyMap<string, string>;
}

/** «Папка» → «Папки», «Альбом 21×30» → «Альбомы 21×30». */
export const itemPluralName = (name: string): string => name.replace(/^Папка/, 'Папки').replace(/^Альбом/, 'Альбомы');

/** Print-file listing order: folders first, albums after; inside one kind the project item order (stable sort). */
export const foldersFirst = <T extends { productKind?: string | null }>(products: readonly T[]): T[] =>
  [...products].sort((a, b) => Number(!isFolderKind(a.productKind)) - Number(!isFolderKind(b.productKind)));

/** One line per item with approved albums; one item, or an album of an unknown item → none (the head alone). */
function itemLines(albums: readonly BulkDownloadAlbum[], items: PrintFilesItems | undefined): string[] {
  if (!items || items.names.size < 2) return [];
  const counts = new Map<string, number>();
  for (const album of albums) {
    const id = album.printProductId || items.main;
    if (!id || !items.names.has(id)) return [];
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  return [...items.names].flatMap(([id, name]) => (counts.has(id) ? [`${name} · ${counts.get(id)}`] : []));
}

function formatLines(lines: readonly PrintFilesLine[]): string[] {
  const byFormat = new Map<string, number>();
  for (const { format, qty } of lines) if (format) byFormat.set(format, (byFormat.get(format) ?? 0) + qty);
  return [...byFormat].map(([format, qty]) => `Копии ${format} · ${qty}`);
}

/** «N … ещё не оплачены» + «<имя> — sum» per student; shown in both switch states. */
function unpaidView(lines: readonly PrintFilesLine[], forms: Forms, verb: Forms): Pick<PrintFilesSection, 'unpaid' | 'rows'> {
  const unpaid = lines.filter(line => !line.paid);
  const qty = qtyOf(unpaid);
  if (qty === 0) return { unpaid: null, rows: [] };
  // By student, not by name: namesakes and every «Имя не задано» are rows of their own.
  const byPerson = new Map<string, { name: string; sum: number }>();
  for (const line of unpaid) {
    const row = byPerson.get(line.personId);
    byPerson.set(line.personId, { name: line.name, sum: (row?.sum ?? 0) + line.sum });
  }

  return {
    unpaid: `${qty} ${pluralRu(qty, forms)} ещё не ${pluralRu(qty, verb)}`,
    rows: [...byPerson.values()].map(({ name, sum }) => `${name} — ${formatMoney(sum)}`),
  };
}

/** `prints: null` = the project has no print orders at all. */
export function printFilesSections(
  data: PrintFilesData,
  includeUnpaid: boolean,
  items?: PrintFilesItems,
): { albums: PrintFilesSection; prints: PrintFilesSection | null } {
  const counted = (lines: readonly PrintFilesLine[]) => lines.filter(line => line.paid || includeUnpaid);

  const countedCopies = counted(data.copies);
  const copies = qtyOf(countedCopies);
  const albumsHead = `${data.albums.length} ${pluralRu(data.albums.length, ALBUM_FORMS)}`;
  const albums: PrintFilesSection = {
    head: copies > 0 ? `${albumsHead} · ${copies} ${pluralRu(copies, COPY_FORMS)}` : albumsHead,
    count: data.albums.length,
    items: itemLines(data.albums, items),
    formats: formatLines(countedCopies),
    ...unpaidView(data.copies, COPY_FORMS, ['оплачена', 'оплачены', 'оплачены']),
  };
  if (data.prints.length === 0) return { albums, prints: null };

  const printed = counted(data.prints);
  const photos = qtyOf(printed);
  const students = new Set(printed.map(line => line.name)).size;
  const prints: PrintFilesSection = {
    head: `${students} ${pluralRu(students, ['ученик', 'ученика', 'учеников'])} · ${photos} фото`,
    count: photos,
    items: [],
    formats: [],
    ...unpaidView(data.prints, ['фото', 'фото', 'фото'], ['оплачено', 'оплачены', 'оплачены']),
  };
  return { albums, prints };
}

const gateKey = (albumId: string, printProductId: string): string => `${albumId}:${printProductId}`;

/** Extra-format copies the archive counts whose copy row is not `approved`; an unread row counts too — the export
 *  refuses it all the same. One entry per album and format. */
export function unapprovedCopies(
  data: PrintFilesData,
  includeUnpaid: boolean,
  variantsByBook: Readonly<Record<string, readonly PhotoBookVariant[]>>,
): UnapprovedCopy[] {
  const albumOf = new Map(data.albums.map(album => [album.personId, album.id]));
  const entries = new Map<string, UnapprovedCopy>();
  for (const { product, format, personId, name, paid } of data.copies) {
    const albumId = personId ? albumOf.get(personId) : undefined;
    if (!product || !format || !albumId || !(paid || includeUnpaid)) continue;
    const state = variantsByBook[albumId]?.find(v => v.printProductId === product)?.state;
    if (state === 'approved') continue;
    const action = state ? GATE_ACTION[state] : null;
    entries.set(gateKey(albumId, product), { albumId, name, format, printProductId: product, action });
  }

  return [...entries.values()];
}

@Component({
  selector: 'app-print-files-dialog',
  imports: [HlmButton, HlmSkeleton, HlmSwitchImports, DialogSheetComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 class="mb-5 text-lg font-bold text-ink">Скачать файлы печати</h2>

        @if (sections(); as view) {
          <div class="mb-5">
            <label class="inline-flex items-center gap-2.5 text-base text-ink">
              <hlm-switch [checked]="includeUnpaid()" (checkedChange)="includeUnpaid.set($event)" />
              Включить неоплаченные заказы
            </label>
          </div>

          <div class="flex flex-col gap-3">
            @for (section of view; track section.key) {
              <section
                class="flex items-center gap-4 rounded-[var(--r-l)] border border-line2 bg-bg2 px-5 py-4"
                [attr.data-section]="section.key"
              >
                <div class="min-w-0 flex-1">
                  <div class="text-base font-semibold text-ink">{{ section.title }}</div>
                  <div class="mt-1 text-sm text-c2">{{ section.head }}</div>
                  @if (section.items.length) {
                    <div class="mt-1.5" data-testid="print-files-items">
                      @for (line of section.items; track line) {
                        <div class="mt-0.5 text-sm text-c2">{{ line }}</div>
                      }
                    </div>
                  }
                  @if (section.formats.length) {
                    <div [class.mt-1.5]="section.items.length">
                      @for (line of section.formats; track line) {
                        <div class="mt-0.5 text-sm text-c2">{{ line }}</div>
                      }
                    </div>
                  }
                  @if (section.unpaid; as unpaid) {
                    <div class="mt-3 text-sm text-ink">{{ unpaid }}</div>
                    @for (row of section.rows; track row) {
                      <div class="mt-0.5 text-sm text-c2">{{ row }}</div>
                    }
                  }
                  @if (section.gate.length) {
                    <div class="mt-3 text-sm text-ink" data-testid="print-files-gate">Сначала утвердите копии:</div>
                    @for (copy of section.gate; track copy.albumId + copy.printProductId) {
                      <div
                        class="mt-1.5 flex items-center justify-between gap-3 text-sm text-ink"
                        [attr.data-copy]="copy.albumId + ':' + copy.printProductId"
                      >
                        <div class="min-w-0">{{ copy.name }}, {{ copy.format }}</div>
                        @switch (copy.action) {
                          @case ('review') {
                            <button
                              hlmBtn
                              type="button"
                              variant="link"
                              class="h-auto p-0"
                              [attr.aria-label]="'Проверить: ' + copy.name + ', ' + copy.format"
                              (click)="review(copy)"
                            >
                              Проверить
                            </button>
                          }
                          @case ('refresh') {
                            <button
                              hlmBtn
                              type="button"
                              [attr.aria-label]="'Обновить: ' + copy.name + ', ' + copy.format"
                              (click)="refresh(copy)"
                            >
                              Обновить
                            </button>
                          }
                          @case ('building') {
                            <button hlmBtn type="button" disabled>Формируем…</button>
                          }
                          @default {
                            <button
                              hlmBtn
                              type="button"
                              [attr.aria-label]="'Повторить: ' + copy.name + ', ' + copy.format"
                              (click)="retry(copy)"
                            >
                              Повторить
                            </button>
                          }
                        }
                      </div>
                    }
                  }
                </div>
                @if (section.count > 0) {
                  <button
                    hlmBtn
                    type="button"
                    [variant]="section.gate.length ? 'blocked' : 'default'"
                    [disabled]="section.gate.length > 0"
                    (click)="download(section.key)"
                  >
                    Скачать
                  </button>
                }
              </section>
            }
          </div>

          <div class="mt-6 flex justify-end">
            <button hlmBtn type="button" variant="outline" (click)="dialogRef.close(undefined)">Закрыть</button>
          </div>
        } @else {
          <div hlmSkeleton class="h-6 w-64 rounded-[var(--r-s)]"></div>
          <div hlmSkeleton class="mt-5 h-32 rounded-[var(--r-l)]"></div>
          <div hlmSkeleton class="mt-3 h-32 rounded-[var(--r-l)]"></div>
        }
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class PrintFilesDialogComponent {
  private readonly ctx = injectBrnDialogContext<PrintFilesDialogContext>();
  protected readonly dialogRef = inject(BrnDialogRef);
  private readonly store = inject(ProjectsStore);
  private readonly opener = inject(HlmDialogOpener);
  private readonly vcr = inject(ViewContainerRef);
  private readonly toast = inject(ToastService);
  private readonly catalog = inject(PrintCatalogFacade);

  protected readonly hostClass = dialogSheetHostClass('project', 'p-9');

  private readonly data = signal<PrintFilesData | null>(null);
  private readonly items = signal<PrintFilesItems | undefined>(undefined);
  protected readonly includeUnpaid = signal(true);
  // «Обновить» pressed: the line reads «Формируем…» until the refresh request settles and the copies are re-read.
  private readonly refreshing = signal<ReadonlySet<string>>(new Set());

  protected readonly sections = computed(() => {
    const data = this.data();
    if (!data) return null;
    const { albums, prints } = printFilesSections(data, this.includeUnpaid(), this.items());
    const refreshing = this.refreshing();
    const gate = unapprovedCopies(data, this.includeUnpaid(), this.store.variantsByBook()).map(copy =>
      refreshing.has(gateKey(copy.albumId, copy.printProductId)) ? { ...copy, action: 'building' as const } : copy,
    );
    const view = [{ key: 'albums' as const, title: 'Альбомы и копии', ...albums, gate }];
    return prints ? [...view, { key: 'prints' as const, title: 'Печать фото', ...prints, gate: [] as UnapprovedCopy[] }] : view;
  });

  constructor() {
    // RACE-SAFE: the context promise is fixed for the dialog's life; one resolution, one write.
    // Copy rows are read before the first paint, so «Скачать» never flips from open to gated under the cursor.
    void this.ctx.data
      .catch((): null => null)
      .then(async data => {
        if (data) await Promise.all([this.loadCopies(unapprovedCopies(data, true, {})), this.loadItems()]);
        this.data.set(data);
      });
  }

  /** The project's items named for the per-item album lines; one item or a failed read → the head alone. */
  private async loadItems(): Promise<void> {
    const projectId = this.store.currentProjectId();
    const base = projectId ? this.store.baseSettingsByProject()[projectId] : undefined;
    const ids = projectPrintItems(base);
    if (ids.length < 2) return;
    const read = await Promise.all(ids.map(id => firstValueFrom(this.catalog.getProduct(id)).catch((): null => null)));
    const products = read.filter(product => !!product);
    if (products.length < ids.length) return;
    const names = new Map(foldersFirst(products).map(product => [product.id, itemPluralName(itemNameOf(products, product))]));
    this.items.set({ main: base?.photoBook?.printProductId, names });
  }

  /** The copies dialog's review: it reads the copy's spreads from the store and approves the copy itself; the
   *  approval re-reads the copy row, and the gate follows. */
  protected review({ albumId, printProductId, format }: UnapprovedCopy): void {
    const projectId = this.store.currentProjectId();
    if (!projectId) return;
    this.opener.open<ApproveAlbumDialogContext, boolean>(ApproveAlbumDialogComponent, this.vcr, {
      spreads: [],
      variant: { projectId, bookId: albumId, printProductId, format },
    });
  }

  /** The copies dialog's refresh: the store re-reads the copies once it is accepted, the gate follows. */
  protected async refresh({ albumId, printProductId }: UnapprovedCopy): Promise<void> {
    const projectId = this.store.currentProjectId();
    if (!projectId) return;
    const key = gateKey(albumId, printProductId);
    this.refreshing.update(set => new Set(set).add(key));
    try {
      await this.store.refreshVariant(projectId, albumId, printProductId);
    } catch (error) {
      toastCopyRefreshFailure(this.toast, error);
    } finally {
      // RACE-SAFE: the overlay only bridges the request; the store's list is the truth once it settles.
      this.refreshing.update(set => {
        const next = new Set(set);
        next.delete(key);
        return next;
      });
    }
  }

  /** An unread copy row: read that album's copies again, the gate follows the store. */
  protected retry(copy: UnapprovedCopy): void {
    void this.loadCopies([copy]);
  }

  private async loadCopies(copies: readonly UnapprovedCopy[]): Promise<void> {
    const projectId = this.store.currentProjectId();
    if (!projectId) return;
    const ids = [...new Set(copies.map(copy => copy.albumId))];
    const reads = await Promise.all(ids.map(id => this.store.loadVariants(projectId, id)));
    if (reads.includes(false)) this.toast.error(RETRY);
  }

  protected download(archive: PrintFilesDialogResult['archive']): void {
    this.dialogRef.close({ archive, includeUnpaid: this.includeUnpaid() } satisfies PrintFilesDialogResult);
  }
}
